import { IProduct, ISelectedOptionItem } from "../common/interfaces";

/**
 * Precio de un renglon del carrito.
 *
 * Antes de esto habia dos calculos independientes (cartView y posStore) que leian
 * `price_1` a pelo. El delta de los modificadores tiene que entrar en los dos o el total
 * de la pantalla no cuadra con el del cajon; el servidor lo recalcula igual en
 * PreciseCalculations, asi que esto es solo el espejo para el cajero.
 */
export const getOptionsPriceDelta = (product: IProduct): number =>
  (product.selected_option_items ?? []).reduce(
    (sum: number, option: ISelectedOptionItem) => sum + (Number(option.price_delta) || 0),
    0
  );

export const getLinePrice = (product: IProduct): number =>
  (Number(product.price_1) || 0) + getOptionsPriceDelta(product);

/**
 * Dos renglones del carrito son EL MISMO renglon.
 *
 * La guarda de `!!a` no sobra: `line_uid` es opcional en IProduct, y un `undefined ===
 * undefined` emparejaba entre si a todos los renglones que no tuvieran identidad. Asi se
 * escribia una sola cantidad en todo el carrito. Sin uid, un renglon no es "el mismo" que
 * nadie, ni siquiera que otro sin uid.
 */
export const isSameLine = (a?: string, b?: string): boolean => !!a && a === b;

/**
 * Firma de la combinacion de opciones de un renglon.
 *
 * Dos unidades del mismo producto se juntan en un solo renglon solo si eligieron
 * exactamente lo mismo; combinaciones distintas siempre generan renglones distintos, que es
 * lo que hace posible "una eleccion por unidad" sin llenar el ticket de renglones sueltos.
 */
export const getOptionsSignature = (product: IProduct): string => {
  const items = (product.selected_option_items ?? [])
    .map((option: ISelectedOptionItem) => option.item_id)
    .sort((a: number, b: number) => a - b)
    .join('-');

  return `${product.id}|${items}|${product.selectedOptions ?? ''}`;
};

/**
 * Un renglon obliga a pasar por el flujo de cocina/orden.
 *
 * Vivia duplicado en tres lugares que ya no coincidian entre si (orderOptions solo miraba
 * `options`), asi que una parrillada que trajera unicamente modificadores nuevos no se
 * habria ruteado a cocina desde finish.
 */
export const needsOrderFlow = (product: IProduct): boolean =>
  (product.options?.length ?? 0) > 0 ||
  (product.option_groups?.length ?? 0) > 0 ||
  !!product.grilling_config?.has_grilling_service ||
  !!product.force_kitchen;

/**
 * Recorta el objeto de catalogo a lo que el backend realmente lee.
 *
 * El carrito guarda el producto completo, con `inventory[]` y ahora `option_groups[]`;
 * mandarlo tal cual duplicaria el tamano del payload de la venta.
 */
export const toSalePayload = (product: IProduct) => ({
  id: product.id,
  name: product.name,
  quantity: product.quantity,
  price_1: product.price_1,
  discount: product.discount,
  iva: product.iva,
  ieps: product.ieps,
  fiscal_unit_id: product.fiscal_unit_id,
  fiscal_prod_serv_id: product.fiscal_prod_serv_id,
  is_bulk: product.is_bulk,
  product_type_id: product.product_type_id,
  has_grilling_service: product.has_grilling_service,
  selectedOptions: product.selectedOptions,
  selected_option_items: product.selected_option_items,
  line_uid: product.line_uid,
  grilling_config: product.grilling_config,
  parent_product_id: product.parent_product_id,
  force_kitchen: product.force_kitchen,
  show_in_kitchen: (product as any).show_in_kitchen,
  giftCardConfig: (product as any).giftCardConfig,
});
